export const INCIDENT_PARTICIPANT_ROLES = Object.freeze([
  'involved',
  'affected',
  'witness',
  'accused',
]);

function actorSummary(actor = {}) {
  return {
    staffId: actor.uid || actor.userId || null,
    staffName: actor.displayName || actor.userName || actor.email || 'Staff member',
    email: actor.email || null,
  };
}

export function cleanIncidentText(value, fallback = '') {
  return typeof value === 'string' ? value.trim() : fallback;
}

export function normalizeIncidentParticipants(participants = []) {
  const unique = new Map();
  participants.forEach((participant) => {
    const studentId = participant?.studentId || participant?.id;
    if (!studentId) return;
    const normalized = {
      studentId,
      studentName: participant.studentName || participant.name || 'Learner',
      className: participant.className || participant.class || '',
    };
    if (INCIDENT_PARTICIPANT_ROLES.includes(participant.role)) normalized.role = participant.role;
    unique.set(studentId, normalized);
  });
  return [...unique.values()];
}

export function normalizeIncidentStaff(staff = []) {
  const unique = new Map();
  staff.forEach((member) => {
    const staffId = member?.staffId || member?.userId || member?.uid || member?.id;
    if (!staffId) return;
    unique.set(staffId, {
      staffId,
      staffName: member.staffName || member.userName || member.displayName || member.name || member.email || 'Staff member',
      role: member.role || 'involved',
      email: member.email || null,
    });
  });
  return [...unique.values()];
}

export function incidentCaptureAudit(action, actor, at, detail = {}) {
  return { action, actorId: actor?.uid || actor?.userId || null,
    actorName: actor?.displayName || actor?.userName || actor?.email || 'Staff member', at, ...detail };
}

export function buildIncidentRecord({
  schoolId,
  participants,
  staff = [],
  summary = '',
  narrative = '',
  immediateAction = '',
  occurredAt = null,
  origin = 'manual',
  actor,
  issuanceId = null,
}, createdAt) {
  if (!schoolId) throw new Error('A school is required to create an incident.');
  const normalizedParticipants = normalizeIncidentParticipants(participants);
  if (normalizedParticipants.length === 0) {
    throw new Error('At least one learner is required to create an incident.');
  }
  if (issuanceId && (!actor?.uid || !/^[a-zA-Z0-9-]+$/.test(issuanceId))) throw new Error('The incident submission identity is invalid.');

  const data = {
    schoolId,
    status: 'open',
    summary: summary.trim(),
    narrative: cleanIncidentText(narrative),
    immediateAction: cleanIncidentText(immediateAction),
    occurredAt: occurredAt || createdAt,
    origin,
    participants: normalizedParticipants,
    reportingStaff: actorSummary(actor),
    staff: normalizeIncidentStaff(staff),
    assignedStaff: [],
    recordRefs: [],
    fileRefs: [],
    notes: [],
    actions: [],
    followUp: {
      owner: null,
      dueDate: null,
      notes: '',
      state: 'not-started',
    },
    sealed: true,
    documentation: {
      health: 'not-required',
      discipline: 'not-required',
      staffReport: 'not-required',
    },
    createdBy: actor?.uid || null,
    createdByName: actor?.displayName || actor?.email || 'Staff member',
    createdAt,
    updatedAt: createdAt,
    closedAt: null,
    closedBy: null,
    audit: [incidentCaptureAudit('incident-created', actor, createdAt, { origin })],
    ...(issuanceId ? { issuanceId } : {}),
  };
  return { id: issuanceId ? `incident_${encodeURIComponent(actor.uid)}_${issuanceId}` : null, data };
}

/** Group identity follows the actual recorded learners, independent of input view. */
export async function prepareIncidentCapture({ participants, summary, actor, context, attempts, create }) {
  const people = normalizeIncidentParticipants(participants)
    .sort((a, b) => a.studentId.localeCompare(b.studentId));
  if (people.length < 2) throw new Error('Choose at least two learners to group as one incident.');
  const title = cleanIncidentText(summary, 'Incident');
  const attempt = await attempts.prepare(context, { purpose: 'incident', participants: people, summary: title });
  return create({ schoolId: context.schoolId, participants: people, summary: title,
    origin: 'quick-issue', actor, issuanceId: attempt.issuanceId });
}

export function incidentCaptureReference(reference) {
  if (!reference?.collection || !reference?.recordId) throw new Error('The saved event reference is missing. Retry this recording.');
  return { kind: 'record', ...reference,
    key: ['record', reference.collection, reference.recordId, reference.attachmentId || ''].join(':') };
}
